import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { chatAPI } from '../services/api';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);

  // Fetch initial unread message count
  useEffect(() => {
    if (isAuthenticated() && user) {
      const fetchInitialUnread = async () => {
        try {
          const res = await chatAPI.getConversations();
          const chats = res.data?.chats || res.data?.conversations || [];
          const total = chats.reduce((sum, c) => sum + (c.unreadCount || 0), 0);
          setUnreadCount(total);
        } catch (err) {
          console.error('Failed to fetch initial unread count:', err);
        }
      };
      fetchInitialUnread();
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated() && user) {
      // Initialize socket connection to backend (port 5000)
      const socketUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
      const newSocket = io(socketUrl, {
        transports: ['websocket', 'polling']
      });

      socketRef.current = newSocket;
      setSocket(newSocket);

      // Connection events
      newSocket.on('connect', () => {
        console.log('🔌 Socket connected:', newSocket.id);
        setConnected(true);
        
        // Authenticate with token
        const token = localStorage.getItem('helpbook_token');
        if (token) {
          newSocket.emit('authenticate', { token });
        }
      });

      newSocket.on('authenticated', ({ userId }) => {
        console.log('🔌 Socket authenticated:', userId);
      });

      newSocket.on('disconnect', (reason) => {
        console.log('🔌 Socket disconnected:', reason);
        setConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('🔌 Socket connection error:', error);
        setConnected(false);
      });

      // User presence events from backend
      newSocket.on('user:online', ({ userId }) => {
        setOnlineUsers(prev => [...new Set([...prev, userId])]);
      });

      newSocket.on('user:offline', ({ userId }) => {
        setOnlineUsers(prev => prev.filter(id => id !== userId));
      });

      // Global message listener to increment unread counter when not on Chat Page
      newSocket.on('message:receive', (msg) => {
        const onChatPage = window.location.pathname.includes('/chat') || window.location.hash.includes('/chat');
        if (!onChatPage) {
          setUnreadCount(prev => prev + 1);
        }
      });

      // Cleanup on unmount or user change
      return () => {
        newSocket.disconnect();
        setSocket(null);
        setConnected(false);
        socketRef.current = null;
      };
    }
  }, [isAuthenticated, user]);

  // Socket helper functions
  const emit = (event, data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    } else {
      console.warn('Socket not connected, cannot emit:', event);
    }
  };

  const sendMessage = (receiverId, message) => {
    emit('message:send', { receiverId, message });
  };

  const onMessageReceive = (callback) => {
    if (socketRef.current) {
      socketRef.current.on('message:receive', callback);
    }
  };

  const onMessageSent = (callback) => {
    if (socketRef.current) {
      socketRef.current.on('message:sent', callback);
    }
  };

  const offMessages = () => {
    if (socketRef.current) {
      socketRef.current.off('message:receive');
      socketRef.current.off('message:sent');
    }
  };

  // Typing indicators
  const startTyping = (receiverId) => {
    emit('typing:start', { receiverId });
  };

  const stopTyping = (receiverId) => {
    emit('typing:stop', { receiverId });
  };

  const value = {
    socket,
    connected,
    onlineUsers,
    unreadCount,
    setUnreadCount,
    emit,
    sendMessage,
    onMessageReceive,
    onMessageSent,
    offMessages,
    startTyping,
    stopTyping,
    isUserOnline: (userId) => onlineUsers.includes(userId)
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
